import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux"; // Remove useDispatch
import { motion, AnimatePresence } from "framer-motion";
import "./InstantServicesPage.css";
import { getFacebookLink, getLinkedInLink, getWhatsAppLink } from "../../utils";
import SearchInput from "../../common/search-input/SearchInput";

// --- Sub-component for 3-line Expand/Collapse ---
const ExpandableHtmlBlock = ({ title, htmlContent }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  if (!htmlContent) return null;

  return (
    <div className="lh-instasrv-html-block">
      <h4>{title}</h4>
      <div
        style={{ height: isExpanded ? "auto" : "40px" }}
        className={`lh-instasrv-html-content ${isExpanded ? "expanded" : "collapsed"}`}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
      <button className="lh-instasrv-toggle-btn" onClick={handleToggle}>
        {isExpanded ? "Show Less" : "Load More"}
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          style={{
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    </div>
  );
};

// --- Main Page Component ---
const InstantServicesPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Get data from Redux store (already fetched by DataFetcher)
  const allServices = useSelector(
    (state) => state.instantServicesList?.services || [],
  );
  const isLoading = useSelector(
    (state) => state.instantServicesList?.isLoading || false,
  );
  const error = useSelector(
    (state) => state.instantServicesList?.error || null,
  );

  const [services, setServices] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const STORAGE_URL = `${process.env.REACT_APP_URL}/storage/`;

  // --- Framer Motion Variants ---
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.9, rotateX: -10 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
    hover: {
      y: -10,
      scale: 1.02,
      boxShadow: "0px 20px 40px rgba(168, 85, 247, 0.2)",
      borderColor: "rgba(168, 85, 247, 0.5)",
      transition: { duration: 0.3 },
    },
  };

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => setActiveSearch(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search or slug changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch, slug]);

  // Filter services by slug and status (and search)
  useEffect(() => {
    let filtered = allServices.filter(
      (item) => item.type === slug && item.status === "active",
    );

    // Apply search filter
    if (activeSearch) {
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(activeSearch.toLowerCase()) ||
          item.decription?.toLowerCase().includes(activeSearch.toLowerCase()),
      );
    }

    setServices(filtered);
  }, [allServices, slug, activeSearch]);

  // Pagination calculations
  const totalPages = Math.ceil(services.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentServices = services.slice(indexOfFirstItem, indexOfLastItem);

  const stopPropagation = (e) => e.stopPropagation();
  const onChangeSearchInput = (e) => {
    setSearchInput(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="lh-instasrv-page-wrapper">
      {/* Cinematic Background */}
      <div className="lh-bg-blobs">
        <motion.div
          animate={{ scale: [1, 1.5, 1], x: [0, 150, 0], y: [0, 100, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="blob blob-1"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], x: [0, -150, 0], y: [0, -100, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="blob blob-2"
        />
        <motion.div
          animate={{ opacity: [0.1, 0.3, 0.1], rotate: [0, 360] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="blob blob-3"
        />
        <div className="lh-noise-overlay"></div>
      </div>

      <AnimatePresence>
        {isLoading && (
          <motion.div exit={{ opacity: 0 }} className="lh-instasrv-full-loader">
            <div className="lh-holographic-spinner"></div>
            <p>Synchronizing Data...</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lh-instasrv-container">
        <motion.header
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lh-instasrv-header"
        >
          <div className="header-badge">Instant Access</div>
          <h1>Explore Cards</h1>
          <p>Next-generation financial solutions curated for your lifestyle.</p>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="lh-search-wrapper"
        >
          <SearchInput
            onChange={onChangeSearchInput}
            value={searchInput}
            placeholder={`Search for ${slug?.replace("_", " ")}s...`}
          />
        </motion.div>

        {error && <div className="lh-instasrv-error">{error}</div>}

        {!isLoading && !error && services.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="lh-instasrv-nodata"
          >
            <div className="nodata-icon">🪐</div>
            <h3>Void Detected</h3>
            <p>
              No services found{" "}
              {activeSearch
                ? `matching "${activeSearch}"`
                : "for this category"}
              .
            </p>
          </motion.div>
        ) : (
          <>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="lh-instasrv-grid"
            >
              {currentServices.map((item) => (
                <motion.div
                  key={item.id}
                  variants={cardVariants}
                  whileHover="hover"
                  className="lh-instasrv-card"
                  onClick={() =>
                    navigate(`/instant-services-detail/${item.id}`)
                  }
                >
                  <div className="lh-instasrv-img-wrap">
                    <div className="img-overlay"></div>
                    <img
                      src={item.item_image_url}
                      alt={item.title}
                      className="lh-instasrv-img"
                    />
                    {item.bank && item.bank.bank_logo && (
                      <div className="lh-instasrv-bank-badge">
                        <img
                          src={`${STORAGE_URL}${item.bank.bank_logo}`}
                          alt={item.bank.bank_name}
                        />
                      </div>
                    )}
                  </div>

                  <div className="lh-instasrv-body">
                    <h2 className="lh-instasrv-title">{item.title}</h2>
                    {item.decription && (
                      <div className="lh-instasrv-desc">{item.decription}</div>
                    )}

                    <div className="lh-html-blocks-container">
                      <ExpandableHtmlBlock
                        title="Documents"
                        htmlContent={item.require_documents}
                      />
                    </div>
                  </div>

                  <div className="lh-instasrv-footer">
                    <motion.a
                      whileTap={{ scale: 0.95 }}
                      href={item.apply_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lh-instasrv-apply-btn"
                      onClick={stopPropagation}
                    >
                      <span>Apply Now</span>
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </motion.a>

                    <div className="lh-instasrv-socials">
                      <a
                        href={getWhatsAppLink(item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-glass"
                        onClick={stopPropagation}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="currentColor"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                        </svg>
                      </a>
                      <a
                        href={getFacebookLink(item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-glass"
                        onClick={stopPropagation}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="currentColor"
                        >
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      </a>
                      <a
                        href={getLinkedInLink(item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-glass"
                        onClick={stopPropagation}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="currentColor"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="help-pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="nav-btn"
                >
                  Prev
                </button>
                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      className={`button-counter ${currentPage === i + 1 ? "active" : ""}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="nav-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InstantServicesPage;
